import * as generatedAdminApi from "./generated/admin-api";
import { adminFetch } from "./generated/mutator";
import type {
  DeliveryPackPDFRequest,
  UpdateAPIActivitySettingsRequest,
  ListActivationsParams,
  ListAuditEventsParams,
  ListLicenseTimelineParams,
  ListProductKeysParams,
  ListLicensesParams,
  GetProductUsageSnapshotParams,
  GetProductPlatformDistributionParams,
  GetProductAppVersionDistributionParams,
  GetProductLifecycleTrendParams,
} from "./generated/model";
import type {
  AdminIdentity,
  ApiErrorPayload,
  Customer,
  CustomerListResponse,
  CreateCustomerRequest,
  CreateLicenseRequest,
  CreateProductRequest,
  ActivationsResponse,
  AuditResponse,
  KeyBatchResponse,
  LicensePatchRequest,
  License,
  LicenseListResponse,
  Product,
  ProductFeatureCatalogRequest,
  ProductListResponse,
  ProductUsageSnapshot,
  ProductPlatformDistribution,
  ProductAppVersionDistribution,
  ProductLifecycleTrend,
  SearchResponse,
  TimelineResponse,
  AdminAccount,
  AdminAccountListResponse,
  AdminAccountEnrollment,
  PromoteLicenseRequest,
  ProductKeysResponse,
  SeatLimitReductionRequest,
} from "./types";
import type { ProductKeyReplacement } from "./generated/model";

type GeneratedResponse = {
  data: unknown;
  status: number;
  headers: Headers;
};

type GeneratedOperation<TResponse extends GeneratedResponse> = (
  options: RequestInit,
) => Promise<TResponse>;

export class ApiError extends Error {
  readonly status: number;
  readonly code?: string;
  readonly requestId?: string;
  readonly correlationId?: string;
  readonly retryAfterSeconds?: number;

  constructor(
    message: string,
    status: number,
    payload?: ApiErrorPayload,
    retryAfterSeconds?: number,
  ) {
    super(message);
    this.name = "ApiError";
    this.status = status;
    this.code = payload?.error?.code;
    this.requestId = payload?.request_id;
    this.correlationId = payload?.correlation_id;
    this.retryAfterSeconds = retryAfterSeconds;
  }
}

function retryAfterSeconds(header: string | null) {
  if (!header) return undefined;
  const seconds = Number(header);
  return Number.isFinite(seconds) && seconds >= 0 ? Math.ceil(seconds) : undefined;
}

export type AuditFilters = Pick<
  ListAuditEventsParams,
  | "target_type"
  | "target_id"
  | "actor_id"
  | "ip"
  | "action"
  | "outcome"
  | "correlation_id"
  | "from"
  | "to"
>;

function generatedRequestOptions(): RequestInit {
  return {
    credentials: "include",
    headers: { Accept: "application/json" },
  };
}

async function callGenerated<TResponse extends GeneratedResponse>(
  operation: GeneratedOperation<TResponse>,
) {
  try {
    return await operation(generatedRequestOptions());
  } catch {
    throw new ApiError(
      "The admin API could not be reached. Check the service connection and try again.",
      0,
    );
  }
}

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === "object" && value !== null;
}

function asErrorPayload(value: unknown): ApiErrorPayload | undefined {
  return isRecord(value) ? (value as ApiErrorPayload) : undefined;
}

async function request<T>(operation: GeneratedOperation<GeneratedResponse>) {
  const response = await callGenerated(operation);
  const payload = asErrorPayload(response.data);
  if (response.status < 200 || response.status >= 300) {
    throw new ApiError(
      payload?.error?.message ||
        `The admin API returned HTTP ${response.status}.`,
      response.status,
      payload,
    );
  }

  return (
    isRecord(response.data) && "data" in response.data
      ? response.data.data
      : response.data
  ) as T;
}

export const adminApi = {
	async login(username: string, password: string, totpCode: string) {
		const response = await adminFetch<{ data: unknown; status: number; headers: Headers }>("/admin/auth/login", {
			method: "POST",
			headers: { "Content-Type": "application/json" },
			body: JSON.stringify({ username, password, totp_code: totpCode }),
		});
		if (response.status !== 204) {
			throw new ApiError(
				"Authentication failed.",
				response.status,
				undefined,
				retryAfterSeconds(response.headers.get("Retry-After")),
			);
		}
	},

	async startEnrollment(token: string) {
		const response = await adminFetch<{ data: { data?: { otpauth_url?: string } }; status: number; headers: Headers }>("/admin/auth/enrollment/start", {
			method: "POST", headers: { "Content-Type": "application/json" }, body: JSON.stringify({ token }),
		});
		const uri = response.data?.data?.otpauth_url;
		if (response.status !== 200 || !uri) throw new ApiError("Enrollment failed.", response.status);
		return uri;
	},

	async completeEnrollment(token: string, password: string, totpCode: string) {
		const response = await adminFetch<{ data: unknown; status: number; headers: Headers }>("/admin/auth/enrollment/complete", {
			method: "POST", headers: { "Content-Type": "application/json" }, body: JSON.stringify({ token, password, totp_code: totpCode }),
		});
		if (response.status !== 204) throw new ApiError("Enrollment failed.", response.status);
	},

  currentIdentity() {
    return request<AdminIdentity>(generatedAdminApi.currentAdminIdentity);
  },

  adminAccounts() { return request<AdminAccountListResponse>(generatedAdminApi.listAdminAccounts); },
  createAdminAccount(username: string, role: AdminAccount["role"], reason: string) {
    return request<AdminAccountEnrollment>((options) => generatedAdminApi.createAdminAccount({ username, role, reason }, options));
  },
  updateAdminAccountState(id: string, state: "active" | "disabled", reason: string) {
    return request<AdminAccount>((options) => generatedAdminApi.updateAdminAccountState(encodeURIComponent(id), { state, reason }, options));
  },
  updateAdminAccountRole(id: string, role: AdminAccount["role"], reason: string) {
    return request<AdminAccount>((options) => generatedAdminApi.updateAdminAccountRole(encodeURIComponent(id), { role, reason }, options));
  },
  resetAdminAccountCredentials(id: string, reason: string) {
    return request<AdminAccountEnrollment>((options) => generatedAdminApi.resetAdminAccountCredentials(encodeURIComponent(id), { reason }, options));
  },

  async logout() {
    const response = await adminFetch<{
      data: unknown;
      status: number;
      headers: Headers;
    }>("/admin/auth/logout", { method: "POST" });
    if (response.status !== 204) {
      throw new ApiError(
        "The admin session could not be closed.",
        response.status,
      );
    }
  },

  products() {
    return request<ProductListResponse>(generatedAdminApi.listProducts);
  },

  productUsage(params: GetProductUsageSnapshotParams = {}) {
    return request<ProductUsageSnapshot>((options) =>
      generatedAdminApi.getProductUsageSnapshot(params, options),
    );
  },
  productPlatforms(productId: string, params: GetProductPlatformDistributionParams = {}) {
    return request<ProductPlatformDistribution>((options) => generatedAdminApi.getProductPlatformDistribution(encodeURIComponent(productId), params, options));
  },
  productVersions(productId: string, params: GetProductAppVersionDistributionParams = {}) {
    return request<ProductAppVersionDistribution>((options) => generatedAdminApi.getProductAppVersionDistribution(encodeURIComponent(productId), params, options));
  },
  productLifecycleTrend(productId: string, params: GetProductLifecycleTrendParams = {}) {
    return request<ProductLifecycleTrend>((options) => generatedAdminApi.getProductLifecycleTrend(encodeURIComponent(productId), params, options));
  },

  product(productId: string) {
    return request<Product>((options) =>
      generatedAdminApi.getProduct(encodeURIComponent(productId), options),
    );
  },

  updateProductStatus(
    productId: string,
    status: "active" | "disabled",
    reason: string,
  ) {
    return request<Product>((options) =>
      generatedAdminApi.updateProductStatus(
        encodeURIComponent(productId),
        { status, reason },
        options,
      ),
    );
  },

  updateProductFeatureCatalog(
    productId: string,
    body: ProductFeatureCatalogRequest,
  ) {
    return request<Product>((options) =>
      generatedAdminApi.updateProductFeatureCatalog(
        encodeURIComponent(productId),
        body,
        options,
      ),
    );
  },

  customers() {
    return request<CustomerListResponse>(generatedAdminApi.listCustomers);
  },

  customer(customerId: string) {
    return request<Customer>((options) =>
      generatedAdminApi.getCustomer(encodeURIComponent(customerId), options),
    );
  },

  licenses(params: ListLicensesParams = {}) {
    return request<LicenseListResponse>((options) =>
      generatedAdminApi.listLicenses(params, options),
    );
  },

  search(query: string) {
    const normalizedQuery = query.trim();
    if (!normalizedQuery)
      return Promise.reject(new Error("A search query is required."));
    return request<SearchResponse>((options) =>
      generatedAdminApi.searchAdminEntities({ q: normalizedQuery }, options),
    );
  },

  activations(licenseId: string, params: ListActivationsParams = {}) {
    return request<ActivationsResponse>((options) =>
      generatedAdminApi.listActivations(
        encodeURIComponent(licenseId),
        params,
        options,
      ),
    );
  },

  productKeys(licenseId: string, params: ListProductKeysParams = {}) {
    return request<ProductKeysResponse>((options) =>
      generatedAdminApi.listProductKeys(
        encodeURIComponent(licenseId),
        params,
        options,
      ),
    );
  },

  licenseTimeline(licenseId: string, params: ListLicenseTimelineParams = {}) {
    return request<TimelineResponse>((options) =>
      generatedAdminApi.listLicenseTimeline(
        encodeURIComponent(licenseId),
        params,
        options,
      ),
    );
  },

  auditEvents(filters: AuditFilters = {}, page: Pick<ListAuditEventsParams, "limit" | "cursor"> = {}) {
    const params: ListAuditEventsParams = Object.fromEntries(
      Object.entries({ ...filters, ...page }).filter(([, value]) => Boolean(value)),
    );
    return request<AuditResponse>((options) =>
      generatedAdminApi.listAuditEvents(params, options),
    );
  },

  // The server appends audit.exported before returning bytes, so a failed
  // audit surfaces here as an error and never as a partial file.
  async exportAuditEvents(filters: AuditFilters = {}) {
    const body = { filters: Object.fromEntries(Object.entries(filters).filter(([, value]) => Boolean(value))) };
    const result = await generatedAdminApi.exportAuditEvents(body, { ...generatedRequestOptions(), cache: "no-store", headers: { Accept: "text/csv" } });
    if (result.status < 200 || result.status >= 300) throw new ApiError(asErrorPayload(result.data)?.error?.message || `The admin API returned HTTP ${result.status}.`, result.status, asErrorPayload(result.data));
    if (!(result.data instanceof Blob)) throw new ApiError("The admin API did not return an export file.", 502);
    return { bytes: result.data, filename: downloadFilename(result.headers, "audit-events.csv") };
  },

  updateLicense(licenseId: string, body: LicensePatchRequest) {
    return request<unknown>((options) =>
      generatedAdminApi.updateLicense(
        encodeURIComponent(licenseId),
        body as Parameters<typeof generatedAdminApi.updateLicense>[1],
        options,
      ),
    );
  },

  reduceSeatLimit(licenseId: string, body: SeatLimitReductionRequest) {
    return request<unknown>((options) =>
      generatedAdminApi.reduceSeatLimit(
        encodeURIComponent(licenseId),
        body,
        options,
      ),
    );
  },

  promoteLicense(licenseId: string, body: PromoteLicenseRequest, idempotencyKey: string) {
    return request<unknown>((options) =>
      generatedAdminApi.promoteLicense(
        encodeURIComponent(licenseId),
        body as Parameters<typeof generatedAdminApi.promoteLicense>[1],
        {
          ...options,
          headers: {
            ...options.headers,
            "Idempotency-Key": idempotencyKey,
          },
        },
      ),
    );
  },

  revokeLicense(licenseId: string, reason: string) {
    return request<unknown>((options) =>
      generatedAdminApi.revokeLicense(
        encodeURIComponent(licenseId),
        { reason },
        options,
      ),
    );
  },

  adminDeactivateActivation(activationId: string, reason: string) {
    return request<unknown>((options) =>
      generatedAdminApi.adminDeactivateActivation(
        encodeURIComponent(activationId),
        { reason },
        options,
      ),
    );
  },

  revokeProductKey(keyId: string, reason: string) {
    return request<unknown>((options) =>
      generatedAdminApi.revokeProductKey(encodeURIComponent(keyId), { reason }, options),
    );
  },

  replaceProductKey(
    licenseId: string,
    keyId: string,
    idempotencyKey: string,
    body: { reason: string },
  ) {
    return request<ProductKeyReplacement>((options) =>
      generatedAdminApi.replaceProductKey(
        encodeURIComponent(licenseId),
        encodeURIComponent(keyId),
        body,
        {
          ...options,
          headers: {
            ...options.headers,
            "Idempotency-Key": idempotencyKey,
          },
        },
      ),
    );
  },

  issueKeys(licenseId: string, count: number, reason: string, idempotencyKey: string) {
    return request<KeyBatchResponse>((options) =>
      generatedAdminApi.issueProductKeys(
        { license_id: licenseId, count, reason },
        {
          ...options,
          headers: {
            ...options.headers,
            "Idempotency-Key": idempotencyKey,
          },
        },
      ),
    );
  },

  recordDeliveryPackExport(
    licenseId: string,
    body: {
      batch_id: string;
      artifact_format: "delivery-pack-v1";
      language: "en";
      file_count: 3;
      byte_length: number;
      client_reported_zip_sha256: string;
    },
  ) {
    return request<unknown>((options) =>
      generatedAdminApi.recordDeliveryPackExport(
        encodeURIComponent(licenseId),
        body,
        options,
      ),
    );
  },

  async generateDeliveryPackPDF(licenseId: string, body: DeliveryPackPDFRequest, signal?: AbortSignal) {
    const blob = await request<Blob>((options) => generatedAdminApi.generateDeliveryPackPDF(
      encodeURIComponent(licenseId), body, { ...options, signal, cache: "no-store", headers: { Accept: "application/pdf" } },
    ));
    if (!(blob instanceof Blob) || !blob.type.toLowerCase().startsWith("application/pdf")) {
      throw new ApiError("The admin API did not return a PDF certificate.", 502);
    }
    return new Uint8Array(await blob.arrayBuffer());
  },

  createProduct(body: CreateProductRequest) {
    return request<Product>((options) =>
      generatedAdminApi.createProduct(body, options),
    );
  },

  createCustomer(body: CreateCustomerRequest) {
    return request<unknown>((options) =>
      generatedAdminApi.createCustomer(body, options),
    );
  },

  createLicense(body: CreateLicenseRequest) {
    return request<License>((options) =>
      generatedAdminApi.createLicense(
        body as Parameters<typeof generatedAdminApi.createLicense>[0],
        options,
      ),
    );
  },

  exportIntegrationGuide() {
    return request<Blob>(generatedAdminApi.exportIntegrationGuide);
  },
};

export function apiErrorMessage(error: unknown) {
  return error instanceof Error
    ? error.message
    : "The request failed. Try again.";
}

export type ActivitySettings = Omit<import("./generated/model").APIActivitySettings, "id" | "updated_at" | "updated_by" | "dropped"> & {
  id?: string;
  updated_at?: string;
  updated_by?: string;
  dropped?: number;
  server_time?: string;
};

export type ActivityFilters = Omit<import("./generated/model").APIActivityFilters, "cursor" | "limit">;

type ActivityResponse<T> = { data: T; status: number; headers: Headers };

async function activityRequest<T>(url: string, options?: RequestInit): Promise<{ data: T; headers: Headers }> {
  const response = await adminFetch<ActivityResponse<T>>(url, { ...options, cache: "no-store" });
  if (response.status < 200 || response.status >= 300) {
    throw new ApiError(`The admin API returned HTTP ${response.status}.`, response.status, asErrorPayload(response.data));
  }
  return { data: response.data, headers: response.headers };
}

function activityQuery(filters: Record<string, string | number | boolean | undefined>) {
  const params = new URLSearchParams();
  for (const [key, value] of Object.entries(filters)) if (value !== undefined && value !== "") params.set(key, String(value));
  const query = params.toString();
  return query ? `?${query}` : "";
}

function downloadFilename(headers: Headers, fallback: string) {
  const value = headers.get("Content-Disposition") ?? "";
  const match = /filename="?([^";]+)"?/i.exec(value);
  return match && /^[a-zA-Z0-9._-]+$/.test(match[1]) ? match[1] : fallback;
}

// Activity payloads are never persisted by this client.
export const activityApi = {
  async settings(): Promise<ActivitySettings> {
    const result = await generatedAdminApi.getAPIActivitySettings({ ...generatedRequestOptions(), cache: "no-store" });
    if (result.status < 200 || result.status >= 300) throw new ApiError(`The admin API returned HTTP ${result.status}.`, result.status, asErrorPayload(result.data));
    const data = result.data as import("./generated/model").GetAPIActivitySettings200;
    const settings = data.data?.configuration;
    if (!settings) throw new ApiError("The admin API did not return activity settings.", 502);
    return { ...settings, server_time: data.data?.server_time };
  },
  async updateSettings(body: UpdateAPIActivitySettingsRequest): Promise<ActivitySettings> {
    // Loaded settings include read-only metadata; never serialize that into a mutation.
    const request: UpdateAPIActivitySettingsRequest = {
      enabled: body.enabled,
      scope: body.scope,
      product_ids: body.product_ids,
      retention_hours: body.retention_hours,
      ledger_retention_hours: body.ledger_retention_hours,
      debug_retention_hours: body.debug_retention_hours,
      debug_enabled: body.debug_enabled,
      debug_scope: body.debug_scope,
      debug_product_ids: body.debug_product_ids,
      debug_operations: body.debug_operations,
      debug_starts_at: body.debug_starts_at,
      debug_ends_at: body.debug_ends_at,
      expected_configuration_id: body.expected_configuration_id,
      reason: body.reason,
    };
    const result = await generatedAdminApi.updateAPIActivitySettings(request, { ...generatedRequestOptions(), cache: "no-store" });
    if (result.status < 200 || result.status >= 300) throw new ApiError(`The admin API returned HTTP ${result.status}.`, result.status, asErrorPayload(result.data));
    const data = result.data as import("./generated/model").UpdateAPIActivitySettings200;
    if (!data.data) throw new ApiError("The admin API did not return activity settings.", 502);
    return data.data;
  },
  async list(params: ActivityFilters & { cursor?: string; limit?: number }) {
    const result = params.source_ip
      ? await activityRequest<{ data?: import("./generated/model").APIActivityPage }>("/v1/admin/api-activity/search", {
          method: "POST", headers: { "Content-Type": "application/json" }, body: JSON.stringify(params),
        })
      : await activityRequest<{ data?: import("./generated/model").APIActivityPage }>(`/v1/admin/api-activity${activityQuery(params)}`);
    if (!result.data.data) throw new ApiError("The admin API did not return activity events.", 502);
    return result.data.data;
  },
  async detail(id: string, includeDebug = true) {
    const result = await activityRequest<{ data?: import("./generated/model").APIActivityEvent }>(`/v1/admin/api-activity/${encodeURIComponent(id)}${includeDebug ? "" : "?include_debug=false"}`);
    if (!result.data.data) throw new ApiError("The admin API did not return activity event details.", 502);
    return result.data.data;
  },
  async export(body: { format: "csv" | "json"; filters: ActivityFilters; reason: string }) {
    const result = await generatedAdminApi.exportAPIActivity(body, { ...generatedRequestOptions(), cache: "no-store", headers: { Accept: body.format === "csv" ? "text/csv" : "application/json" } });
    if (result.status < 200 || result.status >= 300) throw new ApiError(`The admin API returned HTTP ${result.status}.`, result.status, asErrorPayload(result.data));
    if (!(result.data instanceof Blob)) throw new ApiError("The admin API did not return an export file.", 502);
    return { bytes: result.data, filename: downloadFilename(result.headers, `api-activity.${body.format}`) };
  },
};
